/**
 * Mapea una unidad de Alta promoción (Marco) al shape de ficha resumen de propiedades.
 * Los campos de destino son los mismos que usa FichaResumenVistaSecciones / Generales.
 */

function hay(v) {
  return v !== null && v !== undefined && String(v).trim() !== "";
}

function parsePrecio(v) {
  if (v == null || v === "") return null;
  const n = Number(String(v).replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function parseHonorariosPct(raw) {
  if (!hay(raw)) return null;
  const m = String(raw).match(/(\d+(?:[.,]\d+)?)\s*%?/);
  if (!m) return null;
  const n = Number(String(m[1]).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function parsePlanta(plantaRaw) {
  const s = String(plantaRaw || "").trim();
  const out = { piso: null, bloque: null, puerta: null };
  if (!s) return out;
  const mBloque = s.match(/bloque\s*([0-9A-Za-z]+)/i);
  if (mBloque) out.bloque = mBloque[1];
  const mPuerta = s.match(/(?:n[ºo°]?\.?\s*|puerta\s*)(\d+)/i);
  if (mPuerta) out.puerta = mPuerta[1];
  const before = s.split("(")[0].trim();
  if (before) out.piso = before;
  else if (!mBloque && !mPuerta) out.piso = s;
  return out;
}

function anoDesdeEntrega(entrega) {
  if (!hay(entrega)) return null;
  const m = String(entrega).match(/(20\d{2})/);
  return m ? Number(m[1]) : null;
}

function estadoInmueble(unidad, promo) {
  const e = String(unidad?.estado || "").toLowerCase();
  if (e === "completa" || e === "ok") return "Obra nueva";
  if (e === "revisar" || e === "falta" || /construcc|obra/.test(e)) return "En construcción";
  if (hay(promo?.entrega)) return "En construcción";
  return "Obra nueva";
}

function buildTitulo(unidad, promo, form = {}) {
  const web = String(promo?.web_titulo || form.tituloWeb || "").trim();
  if (web) return web;
  const tipo = String(unidad?.tipologia || unidad?.tipo_finca || "Vivienda").trim();
  const pob = String(promo?.poblacion || promo?.ubicacion || "")
    .replace(/\s*\(.*?\)\s*/g, "")
    .trim();
  const cap = tipo.charAt(0).toUpperCase() + tipo.slice(1);
  if (pob) return `${cap} en construcción en ${pob}`;
  return cap;
}

function textoCalidades(calidades) {
  if (!Array.isArray(calidades)) return "";
  return calidades
    .map((c) => {
      if (c && typeof c === "object") {
        const apt = String(c.apartado || "").trim();
        const det = String(c.detalle || "").trim();
        if (apt && det) return `${apt}: ${det}`;
        return det || apt;
      }
      return String(c || "").trim();
    })
    .filter(Boolean)
    .join("\n");
}

function textoCondiciones(condiciones) {
  if (!Array.isArray(condiciones)) return "";
  return condiciones
    .map((c) => {
      if (c && typeof c === "object") {
        const k = String(c.clausula || "").trim();
        const v = String(c.valor || "").trim();
        if (k && v) return `${k}: ${v}`;
        return v || k;
      }
      return String(c || "").trim();
    })
    .filter(Boolean)
    .join("\n");
}

function mapPropietarios(list) {
  if (!Array.isArray(list)) return [];
  return list.map((p, i) => ({
    id: p.id || `promo-prop-${i}`,
    contacto_id: p.contacto_id || null,
    nombre: p.nombre || null,
    apellidos: p.apellidos || null,
    telefono: p.telefono || null,
    email: p.email || null,
    nif: p.nif || null,
    porcentaje: p.porcentaje != null ? p.porcentaje : null,
    rol: p.rol || null,
    es_comunicacion: i === 0,
  }));
}

function mapDocumentos(fotosPlanos, archivosNombres) {
  const docs = [];
  const seen = new Set();
  const push = (nombre, grupo) => {
    const n = String(nombre || "").trim();
    if (!n) return;
    const k = n.toLowerCase();
    if (seen.has(k)) return;
    seen.add(k);
    docs.push({
      id: `doc-${docs.length}`,
      nombre_documento: n,
      nombre_original: n,
      url: null,
      tipo: grupo || null,
    });
  };
  (fotosPlanos || []).forEach((g) => {
    String(g?.archivos || "")
      .split(/[,;]+/)
      .forEach((raw) => push(raw.trim(), g?.grupo));
  });
  (archivosNombres || []).forEach((n) => {
    if (/calidades|memoria|contrato|precio|escritura/i.test(String(n))) {
      push(n, "Documentación");
    }
  });
  return docs;
}

function mapFotos(galeriaItems) {
  if (!Array.isArray(galeriaItems)) return [];
  return galeriaItems
    .map((it, i) => {
      if (!it) return null;
      if (typeof it === "string") return { id: `f-${i}`, url: it, orden: i };
      if (it.url) return { id: it.id || `f-${i}`, url: it.url, orden: i, nombre: it.label || null };
      return null;
    })
    .filter(Boolean);
}

/**
 * @param {object} unidad
 * @param {object} opts
 * @param {object} [opts.datos] extracción Marco (promocion, propietarios, …)
 * @param {object} [opts.form] { ref, titulo, tituloWeb, agente }
 * @param {object[]} [opts.fotos] items galería { url, label }
 * @param {string[]} [opts.archivosNombres]
 * @returns {{ p: object, d: object }}
 */
export function mapearUnidadPromocionAFichaResumen(unidad, opts = {}) {
  const datos = opts.datos || {};
  const promo = datos.promocion || {};
  const form = opts.form || {};
  const planta = parsePlanta(unidad?.planta);
  const precio = parsePrecio(unidad?.precio);
  const honPct = parseHonorariosPct(promo.honorarios);
  const calTxt = textoCalidades(datos.calidades);
  const condTxt = textoCondiciones(datos.condiciones);
  const garajeTxt = String(unidad?.garaje_trastero || "").trim();
  const tieneGaraje = /garaje|parking|plaza/i.test(garajeTxt) && !/^no\b/i.test(garajeTxt);
  const tieneTrastero = /trastero/i.test(garajeTxt) && !/sin\s+trastero/i.test(garajeTxt);
  const eqObs = [garajeTxt, calTxt].filter(Boolean).join("\n\n") || null;
  const obsInternas = [condTxt, hay(promo.fase) ? `Fase: ${promo.fase}` : "", hay(promo.entrega) ? `Entrega: ${promo.entrega}` : ""]
    .filter(Boolean)
    .join("\n") || null;

  const titulo = buildTitulo(unidad, promo, form);
  const ref = unidad?.ref || form.ref || "—";
  const fotos = mapFotos(opts.fotos);
  const propietarios = mapPropietarios(datos.propietarios);
  const documentos = mapDocumentos(datos.fotos_planos, opts.archivosNombres);

  const base = {
    id: `promo-unit-${ref}`,
    referencia: ref,
    titulo,
    operacion: "Venta",
    tipo: unidad?.tipologia || unidad?.tipo_finca || null,
    estado_inmueble_nombre: estadoInmueble(unidad, promo),
    ano_construccion: anoDesdeEntrega(promo.entrega),
    num_dormitorios: unidad?.dorm ?? null,
    num_banos: unidad?.banos ?? null,
    piso: planta.piso,
    bloque: planta.bloque,
    puerta: planta.puerta,
    altura_planta: planta.piso,
    m2_construidos: unidad?.m2 ?? null,
    planos: fotos.some((f) => /plano/i.test(String(f.nombre || f.url || ""))) || null,
    eq_garaje: tieneGaraje || null,
    eq_trastero: tieneTrastero || null,
    eq_piscina_comun: /piscina/i.test(calTxt) || /piscina/i.test(String(promo.web_desc_larga_es || "")) || null,
    eq_zonas_comunes: /zonas?\s+comunes|jardines?/i.test(calTxt + " " + (promo.web_desc_larga_es || "")) || null,
    eq_observaciones: eqObs,
    direccion_completa: promo.direccion_parcela || promo.ubicacion || null,
    direccion_extra: unidad?.planta || null,
    poblacion: promo.poblacion || null,
    poblacion_nombre: promo.poblacion || null,
    zona: promo.zona || null,
    web_titulo: promo.web_titulo || form.tituloWeb || null,
    web_desc_larga_es: promo.web_desc_larga_es || null,
    publicar_web: true,
    precio_venta: precio,
    honorarios_porcentaje: honPct,
    agente_nombre: form.agente || null,
    alias: form.titulo || promo.nombre || null,
    observaciones_internas: obsInternas,
    fotos,
    videos: [],
    superficies: [],
    propietarios,
    contactos_asociados: [],
    documentos,
    contacto_comunicacion_id: propietarios[0]?.contacto_id || propietarios[0]?.id || null,
  };

  return { p: base, d: base };
}

export default mapearUnidadPromocionAFichaResumen;
